import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  faFile as faFileSolid,
  faFileMedicalAlt as faFileMedicalAltSolid,
  faInfoCircle
} from '@fortawesome/fontawesome-pro-solid';
import {faFile, faFileMedicalAlt} from '@fortawesome/fontawesome-pro-regular';
import {
  deduplicateArray,
  addContactIcons,
  orderData,
  validateObjectProperties
} from 'libs/salmon-utils';
import {createColumnHelper} from '@tanstack/react-table';
import {difference} from 'lodash';
import {css} from 'aphrodite';
import {findIconDefinition} from '@fortawesome/fontawesome-svg-core';
import {ToolTipCell} from 'components/ToolTipCell';
import Markdown from '../Markdown';
import './styles.scss';
import {CoverageSummary as CoverageSummaryNew} from './Cell/CoverageSummary/index';
import CoverageSummary from './CoverageSummary';
import {DocumentsCell} from './Cell/DocumentsCell';
import {HighlightRowCell} from './Cell/HighlightRowCell';
import {PdfLink} from './Cell/PdfLink';

const absoluteLink = /^https?:\/\//i;

/**
 * @deprecated Use DocumentsCell from ./Cell/DocumentsCell instead
 * Creates a document link with an appropriate icon based on the document type.
 *
 * @param {string} title - The title of the document to display
 * @param {string} resourceUrl - The URL of the document
 * @param {function} handleDocumentClick - Click handler for the document link
 * @returns {JSX.Element} A link element with an icon and title
 */
export const salmonDocumentLink = (title, resourceUrl, handleDocumentClick) => {
  switch (title) {
    case 'PA Form':
    case 'PA Criteria':
      return (
        <a onClick={handleDocumentClick} href={resourceUrl} rel='noopener noreferrer'>
          <FontAwesomeIcon icon={faFileMedicalAltSolid} />{title}
        </a>
      );
    case 'Coverage Document':
    default:
      return (
        <a onClick={handleDocumentClick} href={resourceUrl} rel='noopener noreferrer'>
          <FontAwesomeIcon icon={faFileSolid} />{title}
        </a>
      );
  }
};

/**
 * @deprecated Use getDocumentsCellRender instead
 * Formatter function for salmon documents in the coverage table
 *
 * @param {*} value - The cell value (unused)
 * @param {Object} options - The options object containing rowData
 * @param {Object} options.rowData - The row data containing salmonDocuments
 * @returns {JSX.Element} A div containing document links with line breaks
 */
export const formatterSalmonDocuments = (value, {rowData}, handleDocumentClick = null) => {
  // Remove duplicates if both title and resourceUrl are the same
  const uniqueDocuments = deduplicateArray(
    rowData.salmonDocuments,
    (doc) => `${doc.title}-${doc.resourceurl}`
  );

  return (
    <div>{uniqueDocuments.map((el) => (
      <React.Fragment key={`${el.title}-${el.resourceurl}`}>
        {salmonDocumentLink(el.title, el.resourceurl, handleDocumentClick)}
        <br/>
      </React.Fragment>
    ))}</div>
  );
};

export function sortProducts(products, sortOrder) {
  return products.sort((a, b) => sortOrder.indexOf(a.id) - sortOrder.indexOf(b.id));
}

/** @deprecated Use getCustomDisplayCellRender instead */
export function triStateDisplay(field, defaultValue = 'Unspecified') {
  return field === true
    ? 'Yes'
    : ((field === false) ? 'No' : defaultValue);
}

const columnHelper = createColumnHelper();

const planTypeHeaderRender = ({settings, styleSheet, label}) => {
  if (settings.content.planTypeHelpHover.length) {
    return (
      <span>
        {label}
        <ToolTipCell
          text={settings.content.planTypeHelpHover}
          placeholder={(<FontAwesomeIcon data-testid="plantype-tooltip" icon={faInfoCircle}/>)}
          placement="bottom"
          styleSheet={styleSheet}
          id="plantype-tooltip"
        />
      </span>
    )
  }

  return label;
}

/**
 * @deprecated Use getHighlightRowCellRender instead
 */
const getPayerCellRender = ({settings, styleSheet, openDisclaimerModal, rowData, value}) => {
  let showHighlightRowDisclaimer = false;
  if (settings.table.highlightRowCriteria) {
    for (const rowCriteria of settings.table.highlightRowCriteria) {
      if (validateObjectProperties(rowData, rowCriteria)) {
        showHighlightRowDisclaimer = true;
        break;
      }
    }
  }

  if (rowData.verballyConfirmed || showHighlightRowDisclaimer) {
    return (
      <div>
        <p>{value}</p>
        <div className={css(styleSheet.highlightRowPara)}>
          <a className={css(styleSheet.highlightRowDisclaimer)} onClick={openDisclaimerModal}>
            <Markdown>{settings.table.highlightRow.disclaimerText}</Markdown>
          </a>
        </div>
      </div>
    );
  }

  return value;
}

/**
 * @deprecated Use getArrayCellRender instead
 */
const getStatesCellRender = ({styleSheet, rowData, value}) => {
  if (value.length > 5) {
    return (
      <ToolTipCell
        text={value.join(', ')}
        placement={'bottom'}
        styleSheet={styleSheet}
        placeholder={`${value.slice(0, 5).join(', ')} ...`}
        id={`${rowData.id}-states`}
      />
    );
  }

  return (<span>{value.join(', ')}</span>);
}

/**
 * @deprecated Use getDocumentsCellRender instead
 */
const getDocumentUrlCellRender = ({settings, styleSheet, value, handleDocumentClick, rowData, column}) => {
  const shouldHidePaTooltip = column.hidePAFormIfNoPAData || false;
  const hasValidPriorAuthorizationUrl = value.priorAuthorizationUrl && absoluteLink.test(value.priorAuthorizationUrl);
  const hasValidPolicyUrl = value.policyUrl && absoluteLink.test(value.policyUrl);

  return (
    <div>
      {hasValidPriorAuthorizationUrl ?
        <>
          <a
            onClick={handleDocumentClick}
            href={value.priorAuthorizationUrl}
            rel='noopener noreferrer'
          >
            <FontAwesomeIcon icon={faFileMedicalAlt} />
            PA Form
          </a>
          <br/>
        </>
        :
        <>
          {!shouldHidePaTooltip &&
            <>
              <ToolTipCell
                placement={'bottom'}
                styleSheet={styleSheet}
                text={rowData.verballyConfirmed ? settings.content.unavailableDocumentHoverPaForVerballyConfirmed : settings.content.unavailableDocumentHoverPa}
                placeholder={(<span><FontAwesomeIcon icon={faFileMedicalAlt} />PA Form</span>)}
                id={'pa-tooltip'}
              />
              <br/>
            </>
          }
        </>
      }
      {hasValidPolicyUrl ?
        <a
          onClick={handleDocumentClick}
          href={value.policyUrl}
          rel='noopener noreferrer'
        ><FontAwesomeIcon icon={faFile}/>Coverage Document</a>
        :
        <ToolTipCell
          placement={'bottom'}
          styleSheet={styleSheet}
          text={rowData.verballyConfirmed ? settings.content.unavailableDocumentHoverCoverageForVerballyConfirmed : settings.content.unavailableDocumentHoverCoverage}
          placeholder={(<span><FontAwesomeIcon icon={faFile} />Coverage Document</span>)}
          id={'doc-tooltip'}
        />
      }
    </div>
  );
}

/**
 * @deprecated Use getCoverageSummaryTypeCellRender instead
 */
const getCoverageSummaryCellRender = ({handler, rowData, settings, styleSheet}) => {
  return <CoverageSummary
    handler={handler}
    rowData={rowData}
    settings={settings}
    styleSheet={styleSheet}
  />;
}

const getMedicalNecessityChecklistCellRender = ({rowData, handleOpenMedicalCoverage, settings}) => {
  return (
    <a
      id={rowData.id}
      data-testid="open-medical-coverage-link"
      onClick={() => handleOpenMedicalCoverage(settings, rowData)}
    >
      <FontAwesomeIcon icon={faFileSolid} size={'2x'} />
    </a>
  );
}

/**
 * @deprecated Use getNewContactsCellRender instead
 */
const getContactsCellRender = ({value, column, rowData, styleSheet, handleContactInstructionsClick}) => {
  // Get contacts to show in the main table.
  const showContacts = [];
  value.forEach((val) => {
    if (column.displayContacts.includes(val.type)) {
      showContacts.push(val);
    }
  });

  // Add dummy objects for any missing contacts that need to be displayed in the main table.
  if (column.showMissingContacts) {
    const missingContacts = difference(
      column.displayContacts,
      showContacts.map((v) => {
        return v.type;
      })
    );

    missingContacts.forEach((x) => {
      showContacts.push({type: x});
    });
  }

  // Add the icon and tooltip related properties to each contact.
  addContactIcons(orderData(showContacts, 'type', column.sortOrder), column.iconTypes);

  // Show the contact instructions link if we have additional
  // information not already shown on the table.
  let showContactInstructionsLink = (
    (rowData.contactModalData.contactinstructions != null) ||
    (rowData.contactModalData.peertopeer != null) ||
    (rowData.contactModalData.expeditedpa != null)
  );

  // Loop through modalOnlyContacts for contacts not shown on the main table.
  // If there are, set showContactInstructionsLink = true;
  if (!showContactInstructionsLink) {
    const modalOnlyContacts = difference(column.modalContacts, column.displayContacts);
    for (let contactType of modalOnlyContacts) {
      for (let contact of rowData.contacts) {
        if (contact.type === contactType) {
          showContactInstructionsLink = true;
          break;
        }
      }
    }
  }

  let contactInstructions = false;
  if (showContactInstructionsLink && column.contactInstructions) {
    contactInstructions = (<a id={rowData.id} onClick={handleContactInstructionsClick}>Contact Instructions</a>);
  }

  const contacts = showContacts.map((item) => {
    const iconLookup = {prefix: item.iconPrefix, iconName: item.iconName};
    let currentContact = (
      <span key={item.type}>
        <FontAwesomeIcon icon={findIconDefinition(iconLookup)} flip="horizontal"/>
        {item.contact ? item.contact : item.default}<br/>
      </span>
    );

    // Add tooltip hover if tooltip aka title exists.
    if (item.title) {
      currentContact = (
        <ToolTipCell
          key={item.type}
          text={item.title}
          placement={item.tooltipAlign}
          styleSheet={styleSheet}
          placeholder={currentContact}
          id={'contact-tooltip'}
        />
      );
    }

    return currentContact;
  });

  return (
    <div key="contacts">
      {contacts}
      {contactInstructions}
    </div>
  );
}

const priorAuthorizationSorting = (rowA, rowB, columnId) => {
  const a = triStateDisplay(rowA.original[columnId]).toLowerCase();
  const b = triStateDisplay(rowB.original[columnId]).toLowerCase();

  return a.localeCompare(b)
}

const getCoveredSorting = (column) => {
  return (rowA, rowB, columnId) => {
    const aRaw = triStateDisplay(rowA.original[columnId]);
    const bRaw = triStateDisplay(rowB.original[columnId]);

    const a = column.customCoveredDisplay?.mappings?.[aRaw] || aRaw;
    const b = column.customCoveredDisplay?.mappings?.[bRaw] || bRaw;

    return a.localeCompare(b);
  };
};

const getCustomSortingFnMapper = (column) => {
  const sortingFnMap = {
    priorAuthorization: priorAuthorizationSorting,
    covered: getCoveredSorting(column),
  };

  return sortingFnMap[column.property];
}

export function getCoverageColumns({
  props,
  handleDocumentClick,
  handleOpenMedicalCoverage,
  handleContactInstructionsClick,
  openDisclaimerModal
}) {
  const {settings, styleSheet} = props;

  return settings.table.columns.map((column) => {
    const styleName = `tableColumn${column.property}`;
    const cellClass = styleSheet[styleName] ? css(styleSheet[styleName]) : '';
    const customSortingFn = getCustomSortingFnMapper(column);

    const dataTableColumn = {
      enableSorting: column.sortable,
      enableColumnFilter: false,
      sortDescFirst: false,
      meta: {className: cellClass},
      size: 0,
      ...(!!customSortingFn && {sortingFn: customSortingFn}),
      header: () => {
        if (column.property === 'planType') {
          return planTypeHeaderRender({...props, label: column.label});
        }

        return column.label;
      },
      cell: settings.table.dynamicColumns
        ? renderDynamicColumnCell(
          column,
          props
        ) : renderColumnCell(
          column,
          props,
          handleDocumentClick,
          handleOpenMedicalCoverage,
          handleContactInstructionsClick,
          openDisclaimerModal
        )
    };

    return columnHelper.accessor(column.property, dataTableColumn);
  })
}

/**
 * @deprecated Use renderDynamicColumnCell instead
 */
export const renderColumnCell = (
  column,
  props,
  handleDocumentClick,
  handleOpenMedicalCoverage,
  handleContactInstructionsClick,
  openDisclaimerModal
) => {
  return ({cell, row}) => {
    const value = cell.getValue();
    const rowData = row.original;
    if (column.property === 'payer') {
      return getPayerCellRender({...props, openDisclaimerModal, value, rowData});
    }

    if (column.property === 'states') {
      return getStatesCellRender({...props, value, rowData});
    }

    if (column.property === 'documentUrl') {
      return getDocumentUrlCellRender({
        ...props,
        value,
        rowData,
        handleDocumentClick,
        column
      });
    }

    if (column.property === 'salmonDocuments') {
      return formatterSalmonDocuments(value, {rowData}, handleDocumentClick);
    }

    if (column.property === 'coverageSummary') {
      return getCoverageSummaryCellRender({handler: column.handler, rowData, ...props});
    }

    if (column.property === 'medicalNecessityChecklist') {
      return getMedicalNecessityChecklistCellRender({rowData, handleOpenMedicalCoverage, ...props});
    }

    if (column.property === 'contacts') {
      return getContactsCellRender({value, column, rowData, handleContactInstructionsClick, ...props})
    }

    if (column.property === 'indications') {
      return (<span>{value.join(', ')}</span>);
    }

    if (column.property === 'covered') {
      let covered = triStateDisplay(value);
      if (column.customCoveredDisplay) {
        covered = column.customCoveredDisplay.mappings[covered];
      }
      return covered;
    }

    if (column.property === 'priorAuthorization') {
      return triStateDisplay(value);
    }

    return value || '';
  }
};

/**
 * Renders a dynamic column cell based on the column type
 * @param {Object} column - The column object
 * @param {Object} props - The props object
 * @returns {JSX.Element} The rendered column cell
 */
export const renderDynamicColumnCell = (
  column,
  props
) => {
  return ({cell, row}) => {
    const value = cell.getValue();
    const rowData = row.original;

    // Auto-detect type if not specified
    const type = column.type || (Array.isArray(value) ? 'array' : typeof value === 'object' ? 'object' : 'string');

    switch (type) {
      case 'array':
        return getArrayCellRender({...props, value, rowData, column, meta: column.meta || {separator: ', '}});
      case 'object':
        return getObjectCellRender({...props, value, rowData, column});
      case 'coverageSummary':
        return getCoverageSummaryTypeCellRender({...props, value, rowData, column});
      case 'documents':
        return getDocumentsCellRender({...props, value, column});
      case 'contacts':
        return getNewContactsCellRender({...props, value, rowData, column});
      case 'highlightRow':
        return getHighlightRowCellRender({...props, value, rowData, column});
      case 'customDisplay':
        return getCustomDisplayCellRender({...props, value, column});
      case 'pdfLink':
        return getPdfLinkCellRender({...props, value, rowData, column});
      case 'string':
        return value == null ? '' : String(value);
      default:
        return ''
    }
  }
}

/**
 * Renders a custom display cell based on the column type
 * @param {Object} value - The value of the cell
 * @param {Object} column - The column object
 * @returns {JSX.Element} The rendered cell
 */
export const getCustomDisplayCellRender = ({value, column}) => {
  const customDisplay = column.meta?.customDisplay || {};

  return customDisplay.mappings?.[value] ?? value;
}

/**
 * Renders an array cell based on the column type
 */
export const getArrayCellRender = ({styleSheet, rowData, value, column, meta}) => {
  if (!Array.isArray(value) || value.length === 0) {
    return '';
  }

  const separator = meta?.separator || ', ';

  // Handle array of objects (e.g., documentTypes with {id, name})
  const displayValue = value.length > 0 && typeof value[0] === 'object' && value[0].name
    ? value.map((item) => item.name).join(separator)
    : value.join(separator);

  if (meta?.tooltip && value.length > 5) {
    const tooltipId = column?.property ? `tooltip-${rowData.id}-${column.property}` : `tooltip-${rowData.id}`;
    return (
      <ToolTipCell
        text={displayValue}
        placement={'bottom'}
        styleSheet={styleSheet}
        placeholder={`${displayValue.slice(0, 20)} ...`}
        id={tooltipId}
      />
    );
  }

  return (<span>{displayValue}</span>);
}

/**
 * Renders an object cell based on the column type
 */
export const getObjectCellRender = ({value, rowData, column}) => {
  if (!value || typeof value !== 'object') {
    return '';
  }

  // Handle array of objects (like documentTypes)
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '';
    }
    if (value[0]?.name) {
      // Extract names from objects
      return <span>{value.map((item) => item.name).join(', ')}</span>;
    }
  }

  // For plain objects, stringify
  return <span>{JSON.stringify(value)}</span>;
}

/**
 * Renders a link cell based on the column type
 */
export const getDocumentsCellRender = ({settings, styleSheet, value, column}) => {
  return <DocumentsCell
    settings={settings}
    styleSheet={styleSheet}
    value={value}
    column={column}
  />;
}

/**
 * Renders a new contacts cell based on the column type
 * @param {Object} value - The value of the cell
 * @param {Object} column - The column object
 * @param {Object} rowData - The row data
 * @param {Object} styleSheet - The style sheet object
 * @returns {JSX.Element} The rendered cell
 */
export const getNewContactsCellRender = ({value, column, rowData, styleSheet}) => {
  const contacts = column.meta?.contacts?.map((contactMeta) => {
    const icon = findIconDefinition({prefix: contactMeta.icon.prefix, iconName: contactMeta.icon.name});

    // Try to find contact from rowData.fields if it exists, otherwise try rowData[contactMeta.name]
    let contactValue = null;
    if (rowData.fields && Array.isArray(rowData.fields)) {
      const contact = rowData.fields.find((field) => field.fieldName === contactMeta.name);
      contactValue = contact?.value;
    } else if (rowData[contactMeta.name] !== undefined) {
      // If fields array doesn't exist, try accessing directly from rowData
      contactValue = rowData[contactMeta.name];
    }

    let currentContact = (
      <span key={contactMeta.name}>
        <FontAwesomeIcon icon={icon} flip="horizontal"/>
        {contactValue ? contactValue : contactMeta.default}<br/>
      </span>
    );

    // Add tooltip
    currentContact = (
      <ToolTipCell
        key={contactMeta.name}
        text={contactMeta.title}
        placement={'bottom'}
        styleSheet={styleSheet}
        placeholder={currentContact}
        id={`contact-tooltip-${contactMeta.name}`}
      />
    );

    return currentContact;
  });

  return (
    <div key="contacts">
      {contacts}
    </div>
  );
}

/**
 * Renders a coverage summary type cell based on the column type
 */
export const getCoverageSummaryTypeCellRender = ({value, rowData, column, styleSheet}) => {
  return <CoverageSummaryNew
    criteria={value}
    column={column}
    rowData={rowData}
    styleSheet={styleSheet}
  />;
}

export const getHighlightRowCellRender = ({settings, styleSheet, value, rowData}) => {
  const documents = rowData.fields.find((field) => field.fieldName === 'documents');

  if (!documents) {
    return value;
  }

  if (documents.value.length === 1 && documents.value[0].documentTypes.length === 0) {
    return <HighlightRowCell
      settings={settings}
      styleSheet={styleSheet}
      value={value}
      rowData={rowData}
    />;
  }

  return value;
}

export const getPdfLinkCellRender = ({rowData, column, styleSheet}) => {
  return <PdfLink
    rowData={rowData}
    column={column}
    styleSheet={styleSheet}
  />;
};